import { useEffect } from 'react';
import { useAppDispatch } from '../../../../../hooks';
import RadioButton from 'components/formComponents/Base/RadioButton';
import { ApplicationType } from '../../../../../types/PartyApplication/partyApplication';
import { useFormContext } from 'react-hook-form';
import { setApplicationType } from '../../../../../redux/reducer/partyApplicationSlice';
import TextBlock, {
  textBlockType,
} from 'components/formComponents/Base/TextBlock';
import styles from '../../../pages/FormPage.module.scss';
import { StartSectionTexts } from '../../../../../types/PartyApplication/PartyApplicationTexts';

const infoText = `<div>Här kan du som företag eller facklig representant ansöka om omställningsstöd för uppsagda anställda. <br /> 
Du kan läsa mer om villkoren här eller kontakta oss om du behöver hjälp på tel 010-480 91 10 eller ansokan@tsl.se.
</div>`;

type Props = {
  texts?: StartSectionTexts;
};

const ApplicationTypeBlock = ({ texts }: Props) => {
  const dispatch = useAppDispatch();
  const { watch } = useFormContext();
  const watchedApplicationType = watch('applicationType');
  const selectedType =
    watchedApplicationType === undefined ||
    watchedApplicationType === null ||
    watchedApplicationType === ''
      ? undefined
      : (Number(watchedApplicationType) as ApplicationType);
  useEffect(() => {
    if (selectedType !== undefined && !Number.isNaN(selectedType)) {
      dispatch(setApplicationType(selectedType));
    }
  }, [selectedType, dispatch]);

  const getTextBlockProps = (
    value: ApplicationType
  ): {
    text: string;
    type: textBlockType;
  } => {
    switch (value) {
      case ApplicationType.dismissedDueToRedundancyOrContractExpiry:
        return {
          text:
            texts?.dismissedDueToRedundancyOrContractExpiryInfo ??
            `<div>Ansökan gäller en eller flera anställda som sägs upp på grund av arbetsbrist, eller tidsbegränsat anställda oavsett om de sägs upp eller tiden löper ut.</div>
          <br/>
          <div>
          Du kan ansöka för båda typer i samma ansökan förutsatt att de omfattas av samma förhandling.
          </div>`,
          type: 'info',
        };
      case ApplicationType.companyBankruptcy:
        return {
          text:
            texts?.companyBankruptcyInfo ??
            `<div>Ansökan gäller ett företag som gått i konkurs och inte längre har en kontaktperson på företaget för ansökan.</div>`,
          type: 'info',
        };
      case ApplicationType.dismissedDueToIllnessOrInjury:
        return {
          text:
            texts?.dismissedDueToIllnessOrInjuryInfo ??
            `<div>Ansökan gäller en anställd som avslutar sin anställning på grund av sjukdom, antingen genom uppsägning eller överenskommelse.</div>`,
          type: 'info',
        };
      default:
        return { text: '', type: 'info' };
    }
  };

  return (
    <>
      <div
        className={styles.preamble}
        dangerouslySetInnerHTML={{
          __html: texts?.startPreamble ?? infoText,
        }}
      />
      <RadioButton
        name="applicationType"
        header="Välj typ av ansökan"
        options={[
          {
            id: String(
              ApplicationType.dismissedDueToRedundancyOrContractExpiry
            ),
            value: ApplicationType.dismissedDueToRedundancyOrContractExpiry,
            displayValue:
              texts?.dismissedDueToRedundancyOrContractExpiryRadioBtn?.label ??
              'Uppsagda pga arbetsbrist eller avslutade tidsbegränsade',
            sortOrder:
              texts?.dismissedDueToRedundancyOrContractExpiryRadioBtn
                ?.radioSortOrder ?? 1,
          },
          {
            id: String(ApplicationType.companyBankruptcy),
            value: ApplicationType.companyBankruptcy,
            displayValue:
              texts?.companyBankruptcyRadioBtn?.label ?? 'Företagskonkurs',
            sortOrder: texts?.companyBankruptcyRadioBtn?.radioSortOrder ?? 2,
          },
          {
            id: String(ApplicationType.dismissedDueToIllnessOrInjury),
            value: ApplicationType.dismissedDueToIllnessOrInjury,
            displayValue:
              texts?.dismissedDueToIllnessOrInjuryRadioBtn?.label ??
              'Uppsagd pga sjukdom',
            sortOrder:
              texts?.dismissedDueToIllnessOrInjuryRadioBtn?.radioSortOrder ?? 3,
          },
        ]}
      />
      {selectedType !== undefined && !Number.isNaN(selectedType) && (
        <TextBlock
          text={getTextBlockProps(selectedType)?.text || ''}
          type={getTextBlockProps(selectedType)?.type}
        />
      )}
    </>
  );
};

export default ApplicationTypeBlock;
